import { Service, inject } from '@piwikpro/platform';
import { NotificationAttributes, Notifications } from '../Notifications';

export interface ICustomNotificationConfig {
  pending?: Partial<NotificationAttributes> | null
  success?: Partial<NotificationAttributes> | null
  error?: Partial<NotificationAttributes> | null
}

const defaultConfig: {
  pending: NotificationAttributes
  success: NotificationAttributes
  error: NotificationAttributes
} = {
  pending: {
    text: 'components:notifications.update.processing',
    type: 'pending',
    autoCloseSeconds: 0,
  },
  success: {
    text: 'components:notifications.update.success',
    type: 'success',
    autoCloseSeconds: 5,
  },
  error: {
    text: 'components:notifications.update.failed',
    type: 'error',
    autoCloseSeconds: 5,
  },
};

@Service()
export class CustomNotification {
  private config: ICustomNotificationConfig = {};

  constructor(
    @inject('NotificationCrate.notifications') private readonly notifications: Notifications,
  ) { }

  withConfig(config: ICustomNotificationConfig): this {
    this.config = config;

    return this;
  }

  async intercept(req: any, next: any) {
    const hasPending = this.config.pending !== null;
    let id: string | null = null;

    if (hasPending) {
      id = this.notifications.push({
        ...defaultConfig.pending,
        ...this.config.pending,
      });
    }

    try {
      const resp = await next(req);

      if (this.config.success !== null) {
        const successNotification = {
          ...defaultConfig.success,
          ...this.config.success,
        };

        if (id) {
          this.notifications.update(id, successNotification);
        } else {
          this.notifications.push(successNotification);
        }
      }

      return resp;
    } catch (err: any) {
      if (this.config.error !== null || err.name === 'InvalidRequestError') {
        let errorNotification: NotificationAttributes;

        if (err.name === 'InvalidRequestError') {
          errorNotification = {
            text: 'components:notifications.update.invalid',
            type: 'error',
            autoCloseSeconds: 5,
            onClose: req.onNotificationRemove,
          };
        } else {
          errorNotification = {
            ...defaultConfig.error,
            ...this.config.error,
          };
        }

        if (id) {
          this.notifications.update(id, errorNotification);
        } else {
          this.notifications.push(errorNotification);
        }
      }

      throw err;
    }
  }
}
